import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Tag, Task } from "../../types";

export function useSetTaskTags() {
  const queryClient = useQueryClient();

  return async (taskId: string, tagIds: string[]): Promise<boolean> => {
    const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);
    const allTags = queryClient.getQueryData<Tag[]>(queryKeys.tags) ?? [];
    const newTags = allTags.filter((t) => tagIds.includes(t.id));

    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, and
    // the rollback's snapshot guard could not put it back.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((task) =>
            task.id === taskId ? { ...task, tags: newTags } : task,
          )
        : prev,
    );

    const { error } = await supabase.rpc("set_task_tags", {
      p_task_id: taskId,
      p_tag_ids: tagIds,
    });

    if (error) {
      if (previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, previousTasks);
      }
      Sentry.captureException(error, {
        extra: { mutation: "useSetTaskTags", taskId },
      });
      toast.error("Failed to update tags. Please try again.");
      return false;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
